// Voice personas — five stable, device-independent "characters" the cook can
// pick for spoken task announcements (sounds.ts).
//
// The Web Speech voice list is entirely platform-dependent, exposes NO gender
// field, and the genuinely novel voices (Zarvox, Whisper…) exist mainly on
// macOS/Safari. So rather than naming device voices — which wouldn't be
// portable — we offer five fixed personas and resolve each against whatever
// the device has:
//   - Female / Male  → a name-heuristic pick of an English voice (best effort,
//                      since there is no gender metadata), normal pitch.
//   - Robot / Bright → any base voice at a low / high `utterance.pitch`.
//   - Novelty        → a real novelty voice if the platform has one, else a
//                      base voice at an odd pitch.
// Using pitch for the "unusual" three means all five sound distinct even on a
// device that exposes only a single voice.

export type VoicePersona = 'female' | 'male' | 'robot' | 'bright' | 'novelty';

export const VOICE_PERSONAS: ReadonlyArray<{ id: VoicePersona; label: string }> = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'robot', label: 'Robot' },
  { id: 'bright', label: 'Bright' },
  { id: 'novelty', label: 'Novelty' },
];

// Lower-cased name fragments that strongly hint a voice's gender / novelty.
// Best-effort only — SpeechSynthesisVoice carries no gender.
const FEMALE_HINTS = [
  'female',
  'samantha',
  'karen',
  'moira',
  'tessa',
  'victoria',
  'zira',
  'hazel',
  'susan',
  'fiona',
  'serena',
  'allison',
  'ava',
  'kate',
  'catherine',
  'zoe',
  'sara',
];
const MALE_HINTS = [
  'male',
  'daniel',
  'alex',
  'david',
  'mark',
  'oliver',
  'thomas',
  'aaron',
  'arthur',
  'gordon',
  'rishi',
  'james',
  'george',
  'guy',
];
const NOVELTY_HINTS = [
  'zarvox',
  'trinoids',
  'whisper',
  'bells',
  'bad news',
  'good news',
  'bahh',
  'boing',
  'bubbles',
  'cellos',
  'organ',
  'albert',
  'wobble',
  'jester',
  'superstar',
  'rocko',
];

function isEnglish(v: SpeechSynthesisVoice): boolean {
  return /^en(-|$)/i.test(v.lang);
}

function matchHint(
  voices: readonly SpeechSynthesisVoice[],
  hints: readonly string[],
): SpeechSynthesisVoice | undefined {
  for (const hint of hints) {
    const found = voices.find((v) => v.name.toLowerCase().includes(hint));
    if (found) return found;
  }
  return undefined;
}

export interface ResolvedVoice {
  /** Undefined → use the engine default voice. */
  voice?: SpeechSynthesisVoice;
  /** `utterance.pitch` (0–2). Always set; 1 is neutral. */
  pitch: number;
}

/**
 * Resolve a persona against the device's available voices. The natural
 * Female/Male personas prefer LOCAL (offline-safe) English voices; the
 * unusual personas lean on pitch so they work on any device, including one
 * with an empty or single-voice list.
 */
export function resolvePersona(
  persona: VoicePersona,
  voices: readonly SpeechSynthesisVoice[],
): ResolvedVoice {
  const english = voices.filter(isEnglish);
  const localEnglish = english.filter((v) => v.localService);
  const pool = localEnglish.length > 0 ? localEnglish : english.length > 0 ? english : voices;

  const female = matchHint(pool, FEMALE_HINTS);
  const male = matchHint(pool, MALE_HINTS);
  const base = female ?? male ?? pool[0];

  switch (persona) {
    case 'female':
      return { voice: female ?? base, pitch: 1 };
    case 'male':
      return { voice: male ?? base, pitch: 1 };
    case 'robot':
      return { voice: base, pitch: 0.4 };
    case 'bright':
      return { voice: base, pitch: 1.8 };
    case 'novelty': {
      const novelty = matchHint(pool, NOVELTY_HINTS);
      if (novelty) return { voice: novelty, pitch: 1 };
      // No novelty voice on this platform — make it odd with a deep-ish pitch
      // on the opposite-gender base for contrast where possible.
      const contrast = base === female ? (male ?? base) : (female ?? base);
      return { voice: contrast, pitch: 0.6 };
    }
    default:
      return { voice: base, pitch: 1 };
  }
}
